import React from "react";

/**
 * Cloudflare Turnstile widget, rendered explicitly.
 *
 * Renders nothing when the page has no site key (local dev; the backend gate is
 * off in that case too). Calls onToken(token) when a challenge is solved and
 * onToken(null) when the token expires or errors. Tokens are single-use, so the
 * parent must call reset() via the ref after a failed submit before retrying.
 */
const TurnstileWidget = React.forwardRef(({action, onToken}, ref) => {
    const container = React.useRef(null);
    const widgetId = React.useRef(null);
    const siteKey = window.turnstile_site_key;

    React.useEffect(() => {
        if (!siteKey || !container.current) return undefined;

        let cancelled = false;
        let poll = null;

        const render = () => {
            if (cancelled || widgetId.current !== null || !window.turnstile) return;
            widgetId.current = window.turnstile.render(container.current, {
                sitekey: siteKey,
                action: action,
                callback: (token) => onToken(token),
                'expired-callback': () => onToken(null),
                'error-callback': () => onToken(null),
            });
        };

        if (window.turnstile) {
            render();
        } else {
            // api.js loads async/defer; wait for it.
            poll = setInterval(() => {
                if (window.turnstile) {
                    clearInterval(poll);
                    render();
                }
            }, 100);
        }

        return () => {
            cancelled = true;
            if (poll) clearInterval(poll);
            if (widgetId.current !== null && window.turnstile) {
                window.turnstile.remove(widgetId.current);
                widgetId.current = null;
            }
        };
    }, [siteKey, action]);

    React.useImperativeHandle(ref, () => ({
        reset: () => {
            if (widgetId.current !== null && window.turnstile) {
                window.turnstile.reset(widgetId.current);
            }
            onToken(null);
        },
    }));

    if (!siteKey) return null;

    return <div ref={container} style={{margin: '12px 0'}}/>;
});

export default TurnstileWidget;
